import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Wind, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  { label: "Home", path: "/" },
  {
    label: "Services",
    path: "/dust-monitoring-services-london",
    children: [
      { label: "Construction Dust Monitoring", path: "/construction-dust-monitoring-london" },
      { label: "Real-Time Monitoring", path: "/real-time-dust-monitoring" },
      { label: "Planning Conditions", path: "/dust-monitoring-planning-conditions" },
      { label: "PM10 Monitoring", path: "/pm10-monitoring-construction" },
      { label: "PM2.5 Monitoring", path: "/pm25-monitoring-construction" },
      { label: "Demolition Monitoring", path: "/dust-monitoring-demolition-sites" },
      { label: "Complaint Investigation", path: "/dust-monitoring-complaints" },
    ],
  },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-card/90 backdrop-blur-md border-b border-border">
      <div className="container mx-auto flex items-center justify-between py-4 px-4">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
            <Wind className="w-4 h-4 text-primary-foreground" />
          </div>
          <div className="leading-tight">
            <span className="font-serif text-xl text-foreground block tracking-tight">Dust Monitoring</span>
            <span className="text-[10px] text-muted-foreground tracking-[0.25em] uppercase font-medium">London</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.path} className="relative group">
                <Link
                  to={item.path}
                  className="text-[13px] font-medium text-muted-foreground hover:text-primary transition-colors tracking-wide inline-flex items-center gap-1"
                >
                  {item.label}
                  <ChevronDown className="w-3 h-3" />
                </Link>
                <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-card border border-border rounded-xl shadow-lg py-2 w-64">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`block px-4 py-2.5 text-sm hover:bg-forest-light transition-colors ${
                          location.pathname === child.path ? "text-primary font-semibold" : "text-muted-foreground"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`text-[13px] font-medium tracking-wide transition-colors ${
                  location.pathname === item.path ? "text-primary" : "text-muted-foreground hover:text-primary"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a href="tel:08004647823" className="flex items-center gap-2 text-[13px] font-semibold text-primary">
            <Phone className="w-3.5 h-3.5" />
            0800 464 7823
          </a>
          <Link
            to="/request-quote"
            className="bg-accent text-accent-foreground px-5 py-2.5 rounded-full text-[13px] font-semibold hover:bg-terracotta-hover transition-colors"
          >
            Request Quote
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-2 text-foreground"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden border-t border-border bg-card overflow-hidden"
          >
            <div className="container mx-auto py-4 px-4 flex flex-col gap-1">
              {navItems.map((item) =>
                item.children ? (
                  <div key={item.path}>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="flex items-center justify-between w-full text-sm font-medium text-muted-foreground hover:text-primary py-2 transition-colors"
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden pl-4"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setMobileOpen(false)}
                              className="block text-sm text-muted-foreground hover:text-primary py-2 transition-colors"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className="text-sm font-medium text-muted-foreground hover:text-primary py-2 transition-colors"
                  >
                    {item.label}
                  </Link>
                )
              )}
              <a href="tel:08004647823" className="flex items-center gap-2 text-sm font-semibold text-primary mt-2">
                <Phone className="w-4 h-4" />
                0800 464 7823
              </a>
              <Link
                to="/request-quote"
                onClick={() => setMobileOpen(false)}
                className="bg-accent text-accent-foreground px-5 py-2.5 rounded-full text-sm font-semibold text-center hover:bg-terracotta-hover transition-colors mt-1"
              >
                Request Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SiteHeader;
